import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import urlJoin from 'url-join';
import { urls } from 'app-constants';
import { pd } from 'utils';
import { useTitleBuilder } from 'components/views/titleBuilder';
import LoadingOverlay from 'components/common/LoadingOverlay';
import StyledScrollbar from 'components/common/StyledScrollbar';
import TagSelect from 'components/common/TagSelect';
import { regenerateBlockIds } from './utils';

const TAGS_ANY = 'any';
const TAGS_ALL = 'all';

const TitleItemImport = ({
  sourceId,
  targetId,
  formsetName,
  itemComponent,
  onAccept = () => {},
  onCancel = () => {},
}) => {
  const [formsetData, setFormsetData] = useState(null);
  const [isFetching, setIsFetching] = useState(false);

  const fetchFormsetData = useCallback(sourceId => {
    const url = urlJoin(urls.titleFormsetsBase, formsetName, sourceId, '/');
    setIsFetching(true);

    fetch(url, { credentials: 'include' })
      .then(response => {
        setIsFetching(false);
        if (!response.ok) {
          throw new Error(response.statusText);
        }
        return response.json();
      })
      .then(data => {
        setFormsetData(data);
      })
      .catch(err => {
        console.error(err);
      });
  }, []);

  useEffect(() => {
    if (sourceId) fetchFormsetData(sourceId);
  }, [sourceId]);

  const [selectedIds, setSelectedIds] = useState([]);

  const toggleItemSelection = (itemId, bool) => setSelectedIds(oldState => {
    const isSelected = oldState.includes(itemId);
    if (typeof bool === 'undefined') bool = !isSelected;
    const val = oldState.filter(id => id !== itemId);
    if (bool) val.push(itemId);
    return val;
  });

  const [selectedTags, setSelectedTags] = useState([]);
  const handleTagsFilterChange = setSelectedTags;

  const [filterType, setFilterType] = useState(TAGS_ANY);
  const handleFilterTypeChange = evt => setFilterType(evt.target.value);

  const filterFunc = useCallback(item => {
    const f = {
      [TAGS_ANY]: 'some',
      [TAGS_ALL]: 'every',
    }[filterType];

    if (selectedTags.length > 0) {
      if (selectedTags[f](t => item.tags.includes(t))) return true;
      return false;
    }
    return true;
  }, [selectedTags, filterType]);

  const extraItemProps = item => ({
    readOnly: true,
    active: false,
    selectable: true,
    selected: selectedIds.includes(item.itemId),
  });

  const opts = {
    formsetData,
    isSortable: false,
    ItemComponent: itemComponent,
    extraItemProps,
    filterFunc,
    onItemClick: toggleItemSelection,
  };

  const {
    itemsById,
    itemsFiltered,
    renderedItems,
  } = useTitleBuilder(opts);

  const handleSelectAll = pd(() => setSelectedIds(itemsFiltered));
  const handleSelectNone = pd(() => setSelectedIds([]));

  useEffect(() => {
    setSelectedIds(os => os.filter(id => itemsFiltered.includes(id)));
  }, [JSON.stringify(itemsFiltered)]);

  const handleAcceptClick = () => {
    const data = selectedIds.map(id => {
      const excludeKeys = ['id', 'itemId', 'itemIndex', 'parent', 'prefix', 'errors'];
      const out = Object.entries(itemsById[id]).reduce((result, [key, val]) => {
        if (!excludeKeys.includes(key)) {
          result[key] = key === 'structuredContent' ? regenerateBlockIds(val || []) : val;
        }
        return result;
      }, {});
      out.parent = targetId;
      return out;
    });
    onAccept(data);
  };

  return (
    <div className="title-builder-items-container title-builder-import-overlay">
      <StyledScrollbar>
        <div className="title-builder-item disabled flex-spread">
          <div className="text-sm">
            <span>Select: &nbsp;</span>
            <span>
              <a href="#" onClick={handleSelectAll}>All</a>
              <span> &thinsp;|&thinsp; </span>
              <a href="#" onClick={handleSelectNone}>None</a>
            </span>
          </div>

          <div className="d-flex" style={{ gap: 10 }}>
            <button
              className="btn"
              type="button"
              onClick={onCancel}
            >
              Cancel
            </button>

            <button
              className="btn btn-primary"
              type="button"
              onClick={handleAcceptClick}
            >
              Import
            </button>
          </div>
        </div>
        <div className="title-builder-item disabled" style={{ flexWrap: 'wrap' }}>
          <div className="text-sm mb-1" style={{ flex: '0 0 100' }}>
            <span>Filter by Tags: &nbsp;</span>
          </div>
          <div style={{ flex: '0 0 100%' }}>
            <TagSelect
              value={selectedTags}
              onChange={handleTagsFilterChange}
            />
          </div>
          <div style={{ flex: '0 0 100%' }}>
            <label className="form-radio form-inline mr-3">
              <input
                type="radio"
                value={TAGS_ANY}
                checked={filterType === TAGS_ANY}
                onChange={handleFilterTypeChange}
              />
              <i className="form-icon" /> <span className="text-sm">Any of these tags</span>
            </label>
            <label className="form-radio form-inline">
              <input
                type="radio"
                value={TAGS_ALL}
                checked={filterType === TAGS_ALL}
                onChange={handleFilterTypeChange}
              />
              <i className="form-icon" /> <span className="text-sm">All of these tags</span>
            </label>
          </div>
        </div>
        {renderedItems}
        <LoadingOverlay show={isFetching} />
      </StyledScrollbar>
    </div>
  );
};

TitleItemImport.propTypes = {
  sourceId: PropTypes.string,
  targetId: PropTypes.string,
  formsetName: PropTypes.string,
  itemComponent: PropTypes.elementType,
  onAccept: PropTypes.func,
  onCancel: PropTypes.func,
};

export default TitleItemImport;
